import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Calendar, Plus, Search, Filter, Clock, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useConfirmation } from '../hooks/useModals';
import { eventsService, charactersService } from '../services';
import PageContainer from '../components/PageContainer';
import EmptyState from '../components/EmptyState';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Input from '../components/ui/Input';
import { LoadingPage } from '../components/ui/Loading';
import ConfirmationModal from '../components/ui/ConfirmationModal';
import CreateEventModal from '../components/modals/CreateEventModal';
import DeclineEventModal from '../components/modals/DeclineEventModal';
import {
  getEventParticipationStatusBadge,
  getEventParticipationStatusText,
  getEventStatusText,
  getEventTypeIcon
} from '../utils/eventTypeTranslation';
import { isAdminEquivalent } from '../utils/permissions';

const Events = () => {
  const { user } = useAuth();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const { modalState: confirmState, confirm, close: closeConfirm } = useConfirmation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProofModal, setShowProofModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [characters, setCharacters] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: '',
  });

  // Paginação
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 9,
    total: 0,
    totalPages: 0,
  });

  // Estatísticas globais (independentes da página atual)
  const [stats, setStats] = useState({
    scheduled: 0,
    active: 0,
    completed: 0,
  });

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy: 'startDate',
        sortOrder: 'desc',
        ...(filters.search && { search: filters.search }),
        ...(filters.type && { type: filters.type }),
        ...(filters.status && { status: filters.status }),
      };

      const response = await eventsService.getEvents(params);

      setEvents(response.events || []);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.totalPages || 0,
      }));
      if (response.stats) {
        setStats(response.stats);
      }
    } catch (err) {
      console.error('Error loading events:', err);
      setError(err.response?.data?.error || 'Erro ao carregar eventos');
    } finally {
      setLoading(false);
    }
  };

  const loadCharacters = async () => {
    try {
      const response = await charactersService.getMeCharacters();

      setCharacters(response.characters || []);
    } catch (err) {
      console.error('Error loading characters:', err);
    }
  };

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filters]);

  useEffect(() => {
    loadCharacters();
  }, []);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Atualiza um filtro e volta para a primeira página
  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleParticipate = async (eventId, characterId) => {
    try {
      await eventsService.participateInEvent(eventId, {
        characterId,
        status: 'CONFIRMED'
      });
      notify.success('Participação confirmada com sucesso!', 'Confirmado');
      await loadEvents();
    } catch (err) {
      console.error('Error participating:', err);
      notify.error(err.response?.data?.error || 'Erro ao participar do evento', 'Erro');
    }
  };

  const handleDeclineClick = (event) => {
    setSelectedEvent(event);
    setShowDeclineModal(true);
  };

  const handleDeclineConfirm = async (reason) => {
    try {
      setLoading(true);
      // Pegar o primeiro personagem do usuário para o decline
      const characterId = characters[0]?.id;
      if (!characterId) {
        notify.error('Você precisa ter um personagem para declinar um evento', 'Erro');
        return;
      }

      await eventsService.participateInEvent(selectedEvent.id, {
        characterId,
        status: 'DECLINED',
        declineReason: reason || undefined
      });

      notify.success('Ausência informada com sucesso!', '✅ Confirmado');
      setShowDeclineModal(false);
      setSelectedEvent(null);
      await loadEvents();
    } catch (err) {
      console.error('Error declining event:', err);
      notify.error(err.response?.data?.error || 'Erro ao informar ausência', 'Erro');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitProof = (event, participation) => {
    console.log('📸 Abrindo modal de prova:', { event: event.name, participation });
    setSelectedEvent(event);
    // Pass the entire participation object so we can extract characterId
    setSelectedCharacter(participation?.character || participation);
    setShowProofModal(true);
  };

  const handleViewDetails = (event) => {
    navigate(`/app/events/${event.id}`);
  };

  const canSubmitProof = (event, participation) => {
    if (event.status !== 'COMPLETED') return false;
    if (participation && participation.status === 'CONFIRMED') return true;
    return false;
  };

  const handleCompleteEvent = async (eventId) => {
    const confirmed = await confirm({
      title: 'Finalizar Evento',
      message: 'Tem certeza que deseja finalizar este evento? Esta ação não pode ser desfeita.',
      confirmText: 'Finalizar',
      cancelText: 'Cancelar',
      type: 'warning',
      icon: '🏁'
    });

    if (!confirmed) return;

    try {
      setError('');
      const response = await eventsService.completeEvent(eventId);
      notify.success(response.message || 'Evento finalizado com sucesso!', '🏁 Evento Finalizado');
      loadEvents(); // Recarregar eventos
    } catch (err) {
      console.error('Error completing event:', err);
      setError(err.response?.data?.error || 'Erro ao finalizar evento');
    }
  };

  const canCompleteEvent = (event) => {
    return isAdminEquivalent(user?.role) &&
      (event.status === 'SCHEDULED' || event.status === 'ACTIVE');
  };

  const scheduledEvents = stats.scheduled;
  const activeEvents = stats.active;
  const completedEvents = stats.completed;

  if (loading) {
    return (
      <PageContainer title="Eventos" description="Carregando...">
        <div className="flex justify-center items-center h-64">
          <LoadingPage text="Carregando eventos épicos..." />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Eventos do Clã"
      description="Participe, prove sua presença e ganhe pontos épicos!"
      actions={
        isAdminEquivalent(user?.role) && (
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Novo Evento
          </Button>
        )
      }
    >
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card variant="glass" className="relative overflow-hidden group hover:scale-[1.02] transition-transform duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Agendados</p>
                  <h3 className="text-3xl font-bold text-white">{scheduledEvents}</h3>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card variant="glass" className="relative overflow-hidden group hover:scale-[1.02] transition-transform duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-transparent"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Ativos</p>
                  <h3 className="text-3xl font-bold text-white">{activeEvents}</h3>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Card variant="glass" className="relative overflow-hidden group hover:scale-[1.02] transition-transform duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Concluídos</p>
                  <h3 className="text-3xl font-bold text-white">{completedEvents}</h3>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-500/10 border border-red-500/20 text-red-400 px-6 py-4 rounded-xl mb-6"
        >
          <div className="font-medium">{error}</div>
        </motion.div>
      )}

      {/* Filters */}
      <Card variant="glass" className="mb-8">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Filter className="w-5 h-5 text-cyan-400" />
            <h3 className="text-lg font-semibold text-white">Filtros</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              icon={Search}
              placeholder="Buscar eventos..."
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              variant="glass"
            />

            <select
              value={filters.type}
              onChange={(e) => updateFilter('type', e.target.value)}
              className="bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all"
            >
              <option value="" className="bg-slate-800">Todos os tipos</option>
              <option value="PVP" className="bg-slate-800">PvP</option>
              <option value="BC" className="bg-slate-800">Bless Castle</option>
              <option value="MEETING" className="bg-slate-800">Reunião</option>
              <option value="OTHER" className="bg-slate-800">Outro</option>
            </select>

            <select
              value={filters.status}
              onChange={(e) => updateFilter('status', e.target.value)}
              className="bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all"
            >
              <option value="" className="bg-slate-800">Todos os status</option>
              <option value="SCHEDULED" className="bg-slate-800">Agendado</option>
              <option value="ACTIVE" className="bg-slate-800">Ativo</option>
              <option value="COMPLETED" className="bg-slate-800">Concluído</option>
              <option value="CANCELLED" className="bg-slate-800">Cancelado</option>
            </select>
          </div>

          <div className="mt-6 text-sm text-gray-400">
            {pagination.total} evento(s) encontrado(s)
          </div>
        </div>
      </Card>

      {/* Events Grid */}
      {events.length === 0 ? (
        <EmptyState
          icon={Calendar}
          title="Nenhum evento encontrado"
          description="Ainda não há eventos criados ou nenhum corresponde aos filtros aplicados."
          action={
            isAdminEquivalent(user?.role) && (
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeiro Evento
              </Button>
            )
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event, index) => {
            // Get ALL participations from this user
            const userParticipations = event.participations?.filter(p => p.userId === user.id) ||
              event.EventParticipation?.filter(p => p.userId === user?.id) || [];

            // For backward compatibility, keep the first participation as "userParticipation"
            const userParticipation = userParticipations[0];

            return (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Card variant="glass" className="group hover:scale-[1.02] transition-all duration-300 cursor-pointer relative overflow-hidden">
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 to-blue-600/0 group-hover:from-cyan-500/10 group-hover:to-blue-600/10 transition-all duration-300 pointer-events-none"></div>

                  <div className="relative p-6">
                    {/* Event Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-2xl group-hover:scale-110 transition-transform duration-200">
                          {getEventTypeIcon(event.type)}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white group-hover:text-cyan-400 transition-colors duration-200">
                            {event.name}
                          </h3>
                          <div className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium mt-1 ${event.status === 'SCHEDULED' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                              event.status === 'ACTIVE' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                                event.status === 'COMPLETED' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' :
                                  'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                            }`}>
                            {getEventStatusText(event.status)}
                          </div>
                        </div>
                      </div>

                      {event.points > 0 && (
                        <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl px-3 py-2 text-center">
                          <div className="text-lg font-bold text-amber-400">{event.points}</div>
                          <div className="text-xs text-amber-500 font-medium">pontos</div>
                        </div>
                      )}
                    </div>

                    {/* Event Info */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <Clock className="w-4 h-4" />
                        <span>{new Date(event.startDate).toLocaleString('pt-BR')}</span>
                      </div>
                    </div>
                    {/* Participation Status - Show all participations */}
                    {userParticipations.length > 0 && (
                      <div className="mb-4 space-y-2">
                        {userParticipations.length === 1 ? (
                          // Single participation - show as before
                          <>
                            <Badge
                              variant={getEventParticipationStatusBadge(userParticipation.status)}
                              className="w-full justify-center"
                            >
                              {getEventParticipationStatusText(userParticipation.status)}
                            </Badge>

                            {userParticipation.status === 'DECLINED' && (
                              isAdminEquivalent(user?.role) && userParticipation.declineReason ? (
                                <div className="mt-2 text-xs text-orange-400 bg-orange-500/10 p-2 rounded-lg border border-orange-500/30">
                                  <span className="inline-flex items-center gap-1">
                                    <span>🔒</span>
                                    <strong>Motivo da ausência:</strong>
                                  </span>
                                  <span className="ml-1">{userParticipation.declineReason}</span>
                                </div>
                              ) : null
                            )}
                          </>
                        ) : (
                          // Multiple participations - show list
                          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                            <div className="space-y-2">
                              {userParticipations.map(participation => (
                                <div key={participation.id}>
                                  <div className="flex items-center justify-between bg-slate-700/30 rounded-lg p-2">
                                    <div className="flex items-center gap-2">
                                      <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-600 rounded flex items-center justify-center text-white font-bold text-xs">
                                        {participation.character?.name?.[0] || '?'}
                                      </div>
                                      <span className="text-sm text-white font-medium">
                                        {participation.character?.name}
                                      </span>
                                    </div>
                                    <Badge variant={getEventParticipationStatusBadge(participation.status)} size="sm">
                                      {getEventParticipationStatusText(participation.status)}
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(event)}
                        fullWidth
                      >
                        📋 Ver Detalhes
                      </Button>

                      {event.status === 'SCHEDULED' && (
                        <div className="space-y-2">
                          {/* Get character IDs that are already participating */}
                          {(() => {
                            // Filter out characters already confirmed in the event
                            const existingCharacterIds = event.participations
                              .filter(p => p.status === 'CONFIRMED')
                              .map(p => p.characterId);

                            const availableCharacters = (characters || [])
                              .filter(char => !existingCharacterIds.includes(char.id))
                              .filter(char => char.status === 'ACTIVE'); // Only active characters

                            return availableCharacters.length > 0 && userParticipations.length === 0 ? (
                              <>
                                <p className="text-xs text-gray-400 mb-2">
                                  Escolha um personagem para participar:
                                </p>
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      handleParticipate(event.id, e.target.value);
                                      e.target.value = '';
                                    }
                                  }}
                                  className="w-full px-3 py-2 bg-green-500/10 border border-green-500/30 text-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50 transition-all"
                                  defaultValue=""
                                >
                                  <option value="" className="bg-slate-800">✅ Confirmar presença...</option>
                                  {availableCharacters.map(char => (
                                    <option key={char.id} value={char.id} className="bg-slate-800">
                                      {char.name} (Nv. {char.level} • {char.class})
                                    </option>
                                  ))}
                                </select>
                              </>
                            ) : null;
                          })()}

                          {characters.length === 0 && (
                            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2">
                              <p className="text-xs text-amber-400">
                                ⚠️ Crie um personagem primeiro
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Admin Actions */}
                      {canCompleteEvent(event) && (
                        <Button
                          variant="warning"
                          size="sm"
                          onClick={() => handleCompleteEvent(event.id)}
                          fullWidth
                        >
                          🏁 Finalizar Evento
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Paginação */}
      {pagination.totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="flex justify-center items-center mt-8 gap-2"
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
          >
            Anterior
          </Button>

          <div className="flex gap-1">
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              const pageNum = Math.max(1, Math.min(
                pagination.totalPages - 4,
                pagination.page - 2
              )) + i;

              if (pageNum > pagination.totalPages) return null;

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    pageNum === pagination.page
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/50'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages}
          >
            Próxima
          </Button>
        </motion.div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateEventModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={() => {
            setShowCreateModal(false);
            loadEvents();
          }}
        />
      )}

      {/* Decline Event Modal */}
      <DeclineEventModal
        isOpen={showDeclineModal}
        onClose={() => {
          setShowDeclineModal(false);
          setSelectedEvent(null);
        }}
        onConfirm={handleDeclineConfirm}
        event={selectedEvent}
        loading={loading}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmState.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
        icon={confirmState.icon}
      />
    </PageContainer>
  );
};

export default Events;